/**
 * The PO Lines grid's quantity cells: Qty Accepted, Qty Delivered, Qty
 * Rejected, warehouse stock and fulfilment, the OTIF verdict, Qty Outstanding
 * and % Delivered.
 *
 * Moved out of POLineRow.tsx unchanged (CR-7408 rework), which was past the
 * repo's 500-line limit. POLineRow delegates these column ids here.
 */
import { ProgressCell } from './ProgressCell';
import { deliveredQtyOf, orderedQtyOf } from '../../deliveredQty';
import { getOTIFStatusStyle, OTIF_BADGE_SHAPE } from '../../../drilldown/drilldownUtils';
import type { useFormatters } from '../../../../contexts/PreferencesContext';
import type { PurchaseOrderLine } from './types';

type FormatQuantity = ReturnType<typeof useFormatters>['formatQuantity'];

/**
 * One quantity cell of a PO Lines grid row.
 *
 * @param columnId - The column being rendered; one of the ids POLineRow delegates
 * @param line - The row's PO line
 * @param formatQuantity - The user's quantity formatter (dash for no figure)
 * @returns The cell, or null for a column id this module does not own
 */
export function renderQuantityCell(columnId: string, line: PurchaseOrderLine, formatQuantity: FormatQuantity) {
  switch (columnId) {
    // Qty Accepted is what was delivered AND KEPT. The column id stays
    // `received_qty` so saved layouts survive; the value and the sort are both
    // the accepted quantity. (CR-7417 called this column Qty Delivered;
    // CR-7468 gave that name to the gross figure in its own column.)
    case 'received_qty':
      return (<td key={columnId} className="py-3 px-4 text-right"><span className="text-sm font-medium">{formatQuantity(line.accepted_qty)}</span></td>);
    // CR-7468: what arrived, gross. Neutral — it is an input, not a verdict.
    // NO `?? '0'`: null is "no figure" and formatQuantity renders it as a dash,
    // exactly as the Qty Accepted cell above and the % Delivered cell below do.
    // Coercing it put "Qty Delivered 0" beside "Qty Accepted -" on a frozen
    // row — the self-contradicting row this ticket exists to end, one column
    // over (round 1 rework; four of six reviewers, same finding).
    case 'delivered_qty':
      return (<td key={columnId} className="py-3 px-4 text-right"><span className="text-sm font-medium">{formatQuantity(line.delivered_qty)}</span></td>);
    // CR-7468: quiet when there is nothing to report. An amber 0 on every
    // line of a clean order book reads as an alert nobody can act on; the
    // colour is reserved for the rows that actually carry a rejection.
    case 'rejected_qty': {
      // Derived from the SAME un-coerced value the text renders, not from a
      // `|| '0'` of its own: a no-figure row must reach the muted colour
      // because it has no figure, not because a fallback turned it into one.
      const rejected = line.rejected_qty == null ? null : Number(line.rejected_qty);
      const hasRejected = rejected !== null && Number.isFinite(rejected);
      return (
        <td key={columnId} className="py-3 px-4 text-right">
          <span className={`text-sm font-medium ${hasRejected && rejected > 0 ? 'text-orange-600' : 'text-muted-foreground'}`}>
            {formatQuantity(hasRejected ? rejected : null)}
          </span>
        </td>
      );
    }
    case 'delivered_warehouse_qty': {
      // Same null rule as the three quantity cells above, and it is here
      // because the round-3 sweep found this one still coercing: the earlier
      // pass fixed `delivered_qty` and `rejected_qty` and stopped, which is
      // the instance-not-mechanism miss P2 exists to prevent. A frozen row
      // sends null here too (`get_delivered_warehouse_qty`), and a confident
      // blue 0 beside a dashed "Qty Accepted -" is the same contradiction one
      // column over. ONE parse feeds both the text and the colour.
      const warehouse = line.delivered_warehouse_qty == null
        ? null : Number(line.delivered_warehouse_qty);
      const hasWarehouse = warehouse !== null && Number.isFinite(warehouse);
      return (
        <td key={columnId} className="py-3 px-4 text-right">
          <span className={`text-sm font-medium ${hasWarehouse && warehouse > 0 ? 'text-blue-600' : 'text-muted-foreground'}`}>
            {formatQuantity(hasWarehouse ? warehouse : null)}
          </span>
        </td>
      );
    }
    case 'warehouse_fulfilment_pct': {
      // CR-7163 (a): supplier-side Fulfilment % — of what this line still
      // owes, how much is sitting in the supplier's own warehouse ready to
      // ship. Numerator is warehouse stock only; the customer's receipts are
      // deliberately excluded (they already have "% Delivered" for that).
      //
      // barPercent floors, so 100% appears only when warehouse >= outstanding
      // — exactly the grey-out threshold (isFullyInSupplierWarehouse). The
      // bar and the row state therefore cannot disagree.
      return (
        <ProgressCell
          key={columnId}
          value={parseFloat(line.delivered_warehouse_qty || '0') || 0}
          total={parseFloat(line.outstanding_qty || '0') || 0}
          emptyLabel="—"
        />
      );
    }
    // CR-7416: the measured OTIF verdict, from the one function that decides
    // it, so this cell and the drill-down it clicks through to cannot say
    // different things about the same line.
    case 'otif_verdict': {
      const { className, label } = getOTIFStatusStyle(line.is_otif, line.status);
      return (
        <td key={columnId} className="py-3 px-4 text-center">
          <span className={`${OTIF_BADGE_SHAPE} ${className}`}>{label}</span>
        </td>
      );
    }
    case 'outstanding_qty':
      return (<td key={columnId} className="py-3 px-4 text-right"><span className={`text-sm font-medium ${parseFloat(line.outstanding_qty) > 0 ? 'text-orange-600' : 'text-muted-foreground'}`}>{formatQuantity(line.outstanding_qty)}</span></td>);
    // The same number as the Qty Delivered cell, as a proportion — from the
    // same parser, so the two cannot diverge. CR-7417 read received_qty here
    // and put "100% Delivered" beside "Qty Delivered 61"; CR-7468 gives
    // Accepted its own cell and puts this one back on what arrived.
    // A row with no figure renders the SAME dash the quantity cell renders —
    // a 0% bar beside a dash is the contradiction one column over.
    case 'pct_delivered': {
      const delivered = deliveredQtyOf(line);
      const ordered = orderedQtyOf(line);
      if (delivered === null || ordered === null) {
        return (<td key={columnId} className="py-3 px-4 text-center"><span className="text-sm text-muted-foreground">-</span></td>);
      }
      return (
        <ProgressCell key={columnId} value={delivered} total={ordered} />
      );
    }
    default:
      return null;
  }
}
